var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
function formatCell(col, row) {
    const value = row === null || row === void 0 ? void 0 : row[col.id];
    return col.formatter ? col.formatter(value, row) : value;
}
// Excel sheet names: max 31 chars, cannot contain : \ / ? * [ ], must be unique.
function sanitizeSheetName(name, used) {
    let base = (name || 'Sheet').replace(/[:\\/?*\[\]]/g, ' ').trim().substring(0, 31) || 'Sheet';
    let candidate = base;
    let i = 1;
    while (used.has(candidate.toLowerCase())) {
        const suffix = ` (${i++})`;
        candidate = base.substring(0, 31 - suffix.length) + suffix;
    }
    used.add(candidate.toLowerCase());
    return candidate;
}
function triggerBlobDownload(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
/**
 * @export
 * Build a multi-sheet .xlsx workbook in the browser and download it. Pages
 * through each sheet's rows, reports progress, and supports cooperative abort.
 */
export function exportXlsx(options) {
    return __awaiter(this, void 0, void 0, function* () {
        const { sheets, fileName, pageSize, onProgress, signal } = options;
        // xlsx is a large dependency (~900 KiB) — load it on demand from a separate
        // chunk only when an Excel export actually runs, keeping it out of the eager bundle.
        const XLSX = yield import(/* webpackChunkName: "xlsx" */ 'xlsx');
        const grandTotal = sheets.reduce((sum, s) => sum + (s.total || 0), 0) || 1;
        let processed = 0;
        onProgress === null || onProgress === void 0 ? void 0 : onProgress({ isExporting: true, progress: 0, currentPage: 0, totalRows: grandTotal, processedRows: 0 });
        const wb = XLSX.utils.book_new();
        const usedNames = new Set();
        for (const sheet of sheets) {
            const aoa = [sheet.columns.map(c => c.label)];
            let page = 1;
            // eslint-disable-next-line no-constant-condition
            while (true) {
                if (signal === null || signal === void 0 ? void 0 : signal.aborted) {
                    const err = new Error('Export aborted');
                    err.name = 'AbortError';
                    throw err;
                }
                const { items } = yield sheet.fetchPage(page);
                if (!items || items.length === 0)
                    break;
                for (const row of items)
                    aoa.push(sheet.columns.map(c => formatCell(c, row)));
                processed += items.length;
                onProgress === null || onProgress === void 0 ? void 0 : onProgress({
                    isExporting: true,
                    progress: Math.min(99, Math.round((processed / grandTotal) * 100)),
                    currentPage: page,
                    totalRows: grandTotal,
                    processedRows: processed,
                });
                if (items.length < pageSize)
                    break; // short page = last page
                page++;
            }
            const ws = XLSX.utils.aoa_to_sheet(aoa);
            XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(sheet.name, usedNames));
        }
        const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        triggerBlobDownload(blob, `${fileName}.xlsx`);
        onProgress === null || onProgress === void 0 ? void 0 : onProgress({ isExporting: false, progress: 100, currentPage: 0, totalRows: grandTotal, processedRows: processed });
    });
}
